import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';

export type ChatUserType = 'guest' | 'candidate' | 'company' | 'admin';
export type ChatStatus = 'open' | 'pending' | 'closed';
export type ChatSenderType = 'user' | 'bot' | 'admin' | 'system';
export type ChatMessageType = 'text' | 'quick_reply' | 'whatsapp' | 'system';

export interface ChatApiMessage {
  id: number;
  conversation_id: number;
  sender_type: ChatSenderType;
  sender_user_id: number | null;
  message_type: ChatMessageType;
  message_key: string | null;
  message: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string | null;
  time?: string;
}

export interface ChatConversation {
  id: number;
  user_id: number | null;
    user?: any;

  user_type: ChatUserType;
  session_id: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  whatsapp_number: string | null;
  source_page: string | null;
  status: ChatStatus;
  last_message_at: string | null;
  closed_at: string | null;
  messages?: ChatApiMessage[];
  latest_message?: ChatApiMessage | null;
  messages_count?: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface ChatQuickReply {
  id: number;
  icon: string | null;
  label_key: string;
  message_key: string;
  user_type: ChatUserType | 'all';
  is_active: boolean;
  sort_order: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface SendChatBody {
  conversation_id?: number;
  session_id?: string;
  user_type?: ChatUserType;
  name?: string;
  phone?: string;
  email?: string;
  whatsapp_number?: string;
  source_page?: string;
  message_type?: ChatMessageType;
  message_key?: string;
  message?: string;
}

export interface SendChatResponse {
  message: string;
  conversation: ChatConversation;
  user_message: ChatApiMessage;
  bot_message: ChatApiMessage;
}

export interface QuickRepliesResponse {
  quick_replies: ChatQuickReply[];
}

export interface SingleConversationResponse {
  conversation: ChatConversation;
}

export interface PaginatedChatConversations {
  current_page: number;
  data: ChatConversation[];
  first_page_url: string | null;
  from: number | null;
  last_page: number;
  last_page_url: string | null;
  links: any[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private readonly baseUrl = `${environment.Server_URL}/chat`;

  constructor(private http: HttpClient) {}

  // =========================
  // Public Chat
  // =========================

  sendMessage(body: SendChatBody): Observable<SendChatResponse> {
    return this.http.post<SendChatResponse>(`${this.baseUrl}/send`, body);
  }

  getQuickReplies(userType: ChatUserType | 'guest' = 'guest'): Observable<QuickRepliesResponse> {
    const params = new HttpParams().set('user_type', userType);

    return this.http.get<QuickRepliesResponse>(
      `${this.baseUrl}/quick-replies`,
      { params }
    );
  }

  // =========================
  // User Chats
  // =========================

  getMyChats(perPage: any = 20): Observable<PaginatedChatConversations> {
    const params = new HttpParams().set('per_page', perPage);

    return this.http.get<PaginatedChatConversations>(
      `${this.baseUrl}/my-chats`,
      { params }
    );
  }

  getChatsByUserId(userId: any, perPage: any = 20): Observable<PaginatedChatConversations> {
    const params = new HttpParams().set('per_page', perPage);

    return this.http.get<PaginatedChatConversations>(
      `${this.baseUrl}/user/${userId}`,
      { params }
    );
  }

  getConversation(conversationId: any): Observable<SingleConversationResponse> {
    return this.http.get<SingleConversationResponse>(
      `${this.baseUrl}/conversations/${conversationId}`
    );
  }

  closeConversation(conversationId: any): Observable<any> {
    return this.http.patch(
      `${this.baseUrl}/conversations/${conversationId}/close`,
      {}
    );
  }

  // =========================
  // Admin Chats
  // =========================

  getAdminConversations(filters: {
    status?: ChatStatus | '';
    user_type?: ChatUserType | '';
    search?: string;
    per_page?: number;
    page?: number;
  } = {}): Observable<PaginatedChatConversations> {
    let params = new HttpParams();

    Object.keys(filters).forEach((key) => {
      const value = (filters as any)[key];

      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value);
      }
    });

    return this.http.get<PaginatedChatConversations>(
      `${this.baseUrl}/admin/conversations`,
      { params }
    );
  }

  adminReply(conversationId: any, message: string): Observable<{
    message: string;
    conversation: ChatConversation;
    admin_message: ChatApiMessage;
  }> {
    return this.http.post<{
      message: string;
      conversation: ChatConversation;
      admin_message: ChatApiMessage;
    }>(`${this.baseUrl}/admin/conversations/${conversationId}/reply`, {
      message,
    });
  }

  adminUpdateStatus(conversationId: any, status: ChatStatus): Observable<{
    message: string;
    conversation: ChatConversation;
  }> {
    return this.http.patch<{
      message: string;
      conversation: ChatConversation;
    }>(`${this.baseUrl}/admin/conversations/${conversationId}/status`, {
      status,
    });
  }

  // =========================
  // Helpers
  // =========================

  getOrCreateGuestSessionId(): string {
    const key = 'atw_chat_session_id';

    let sessionId = localStorage.getItem(key);

    if (!sessionId) {
      sessionId = `guest_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 10)}`;

      localStorage.setItem(key, sessionId);
    }

    return sessionId;
  }
}